import { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { api } from "../services/api";
import { Link } from "react-router-dom";

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchMessages = async (pageToLoad = 1, status = "", query = "") => {
    try {
      const params = new URLSearchParams({
        page: pageToLoad,
        limit,
        ...(status && { status }),
        ...(query && { q: query }),
      }).toString();

      const res = await api.get(`/api/admin/contact-messages/list?${params}`);
      setMessages(res.data.messages);
      setTotal(res.data.total);
      setPage(res.data.page);
    } catch (err) {
      console.error("Error fetching messages", err);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedMessages = [...messages].sort((a, b) => {
    const aVal = a[sortBy] || "";
    const bVal = b[sortBy] || "";
    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(total / limit);

  const columns = [
    { label: "Date", field: "created_at" },
    { label: "Name", field: "name" },
    { label: "Email", field: "email" },
    { label: "Type", field: "type" },
    { label: "Message", field: "message" },
    { label: "Status", field: "status" },
  ];

  return (
    <Box
      sx={{
        backgroundImage: 'url("/assets/bg-pitchest.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        px: 2,
        py: 6,
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">📬 Contact Messages</Typography>
          <Link
            to="/admin"
            style={{
              color: "#90caf9",
              textDecoration: "none",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            ⬅ Back
          </Link>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Total: {total} — New: {messages.filter((m) => m.status === "new").length} — Page {page} of {totalPages}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box>
            <label style={{ marginRight: 8 }}>Search:</label>
            <input
              type="text"
              placeholder="Search name, email or message"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchMessages(1, statusFilter, searchQuery);
                }
              }}
              style={{
                padding: "6px 10px",
                width: 280,
                borderRadius: 4,
                border: "1px solid #888",
                backgroundColor: "#222",
                color: "#fff",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchMessages(1, statusFilter, searchQuery)}
              sx={{ ml: 1 }}
            >
              🔍 Search
            </Button>
          </Box>

          <Box>
            <label htmlFor="status-filter" style={{ marginRight: 8 }}>
              Filter by status:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                const newStatus = e.target.value;
                setStatusFilter(newStatus);
                fetchMessages(1, newStatus, searchQuery);
              }}
              style={{
                padding: "6px 10px",
                backgroundColor: "#222",
                color: "#fff",
                border: "1px solid #888",
                borderRadius: "4px",
              }}
            >
              <option value="">All</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </Box>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => handleSort(col.field)}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      backgroundColor: "#34163A",
                      color: "white",
                      borderBottom: "2px solid #34163A",
                      cursor: "pointer",
                    }}
                  >
                    {col.label}{" "}
                    {sortBy === col.field && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedMessages.map((msg, idx) => (
                <tr key={msg.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{new Date(msg.created_at).toLocaleString()}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{msg.name}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{msg.email}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{msg.type}</td>
                  <td style={{ padding: "8px", textAlign:"left" , maxWidth: 300 }}>{msg.message}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>
                    <select
                      value={msg.status}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        try {
                          await api.patch(`/api/admin/contact-messages/${msg.id}`, {
                            status: newStatus,
                          });
                          fetchMessages(page);
                        } catch (err) {
                          console.error("Error updating status", err);
                        }
                      }}
                      style={{
                        backgroundColor:
                          msg.status === "new"
                            ? "#b71c1c"
                            : msg.status === "in_progress"
                            ? "#1b5e20"
                            : "#888888",
                        color: "#fff",
                        border: "1px solid #888",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        minWidth: 120,
                        fontWeight: 600,
                      }}
                    >
                      <option style={{ color: "#fff" }} value="new">New</option>
                      <option style={{ color: "#fff" }} value="in_progress">In Progress</option>
                      <option style={{ color: "#fff" }} value="resolved">Resolved</option>
                    </select>
                  </td>
                </tr>
              ))}
              {sortedMessages.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: "16px", textAlign: "center", backgroundColor: "#452654" }}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            disabled={page === 1}
            onClick={() => fetchMessages(page - 1, statusFilter)}
          >
            ⬅ PREV
          </Button>
          <Button
            variant="outlined"
            disabled={page === totalPages || messages.length === 0}
            onClick={() => fetchMessages(page + 1, statusFilter)}
          >
            NEXT ➡
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminContactMessages;
